import { BrandLoader } from '@/components/BrandLoader';
import { isJustLoggedOut, useAuth } from '@/features/auth/useAuth';
import { revealFor } from '@/features/newDesignReveal/revealRules';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type Welcome,
  buildWelcome,
  readLastUser,
  recordWelcome,
  rememberUser,
  today,
  welcomedToday,
} from './welcomeContent';

/**
 * Codzienne powitanie (Dawid 2026-09-30): pierwsze otwarcie CRM danego dnia
 * pokazuje pełny ekran marki z „Cześć, Julia!” i hasłem dnia (~5 s), a aplikacja
 * w tym czasie ładuje się pod spodem. Pamięć per urządzenie (localStorage).
 *
 * Montowane nad AuthGuardem: startuje od pierwszej klatki dla ostatnio
 * zalogowanej osoby, a po odpowiedzi serwera potwierdza (ta sama osoba), podmienia
 * (ktoś inny) albo chowa (sesja wygasła). Karta otwarta od wczoraj też przywita.
 */
const DURATION_MS = 5000;
/** Klik przyspiesza, ale dopiero gdy da się przeczytać powitanie. */
const SKIP_AFTER_MS = 1500;

interface Shown {
  welcome: Welcome;
  day: string;
  recorded: boolean;
}

function welcomeFromCache(): Welcome | null {
  const last = readLastUser();
  if (!last || isJustLoggedOut()) return null;
  if (revealFor(last, window.location.search) || welcomedToday(last.id, today())) return null;
  return buildWelcome(last);
}

function useVisibleDay(): string {
  const [day, setDay] = useState(today);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setDay(today());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  return day;
}

function useDailyWelcome() {
  const { user, error } = useAuth();
  const day = useVisibleDay();
  const [welcome, setWelcome] = useState<Welcome | null>(welcomeFromCache);
  const shown = useRef<Shown | null>(welcome ? { welcome, day, recorded: false } : null);

  useEffect(() => {
    const current = shown.current;
    const dropUnconfirmed = () => {
      if (current && !current.recorded) {
        shown.current = null;
        setWelcome(null);
      }
    };
    if (error) return dropUnconfirmed();
    if (!user) return;
    // Po kliknięciu „Wyloguj” nie witamy na ekranie „Wylogowano”.
    if (isJustLoggedOut()) return dropUnconfirmed();
    rememberUser(user);
    if (current && current.welcome.userId === user.id && current.day === day) {
      if (!current.recorded) recordWelcome(current.welcome, day);
      current.recorded = true;
      return;
    }
    if (revealFor(user, window.location.search) || welcomedToday(user.id, day)) {
      return dropUnconfirmed();
    }
    const next = buildWelcome(user);
    recordWelcome(next, day);
    shown.current = { welcome: next, day, recorded: true };
    setWelcome(next);
  }, [user, error, day]);

  const dismiss = useCallback(() => setWelcome(null), []);
  return { welcome, dismiss };
}

export function DailyWelcome() {
  const { welcome, dismiss } = useDailyWelcome();
  const [exiting, setExiting] = useState(false);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    if (!welcome) return;
    setExiting(false);
    setCanSkip(false);
    const skipTimer = setTimeout(() => setCanSkip(true), SKIP_AFTER_MS);
    const exitTimer = setTimeout(() => setExiting(true), DURATION_MS);
    return () => {
      clearTimeout(skipTimer);
      clearTimeout(exitTimer);
    };
  }, [welcome]);

  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(dismiss, 400);
    return () => clearTimeout(t);
  }, [exiting, dismiss]);

  if (!welcome) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ${
        exiting ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
      onClick={() => canSkip && setExiting(true)}
    >
      <BrandLoader
        durationMs={DURATION_MS}
        tagline=""
        greeting={welcome.greeting}
        message={welcome.message}
      />
      {canSkip && !exiting && (
        <p className="brand-rise pointer-events-none absolute inset-x-0 bottom-8 text-center text-xs text-fg-subtle">
          Kliknij, aby przejść dalej
        </p>
      )}
    </div>
  );
}
